import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import {
  Shield, Search, CheckCircle, XCircle, RefreshCw, Lock,
  Eye, X, ChevronLeft, ChevronRight, Copy, Check, Send, Download,
} from 'lucide-react';
import Layout from '../../components/Layout/Layout';

/* ────────────────────────────────────────────────────────────── */
/*  Paleta de acciones                                            */
/* ────────────────────────────────────────────────────────────── */
const ACCIONES_COLOR = {
  INSERT:           'bg-green-100 text-green-700 border-green-200',
  UPDATE:           'bg-blue-100 text-blue-700 border-blue-200',
  DELETE:           'bg-red-100 text-red-700 border-red-200',
  PROMOVER_ROL:     'bg-purple-100 text-purple-700 border-purple-200',
  INSERT_PROMOTOR:  'bg-teal-100 text-teal-700 border-teal-200',
  LOGIN:            'bg-gray-100 text-gray-600 border-gray-200',
  LOGOUT:           'bg-gray-100 text-gray-600 border-gray-200',
  RESOLVER:         'bg-orange-100 text-orange-700 border-orange-200',
  VALIDAR_CAPTURA:  'bg-rose-100 text-rose-700 border-rose-200',
  INSERT_DUPLICADO: 'bg-amber-100 text-amber-700 border-amber-200',
};

const ITEMS_POR_PAGINA = 20;

/* ────────────────────────────────────────────────────────────── */
/*  Helpers                                                       */
/* ────────────────────────────────────────────────────────────── */
const esIPLocal = (ip) =>
  !ip || ip === '::1' || ip === '127.0.0.1' || ip.startsWith('::ffff:127.');

const formatearFecha = (f) => {
  if (!f) return '—';
  try {
    return new Date(f).toLocaleString('es-MX', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '—'; }
};

const formatearJSON = (valor) => {
  if (valor === null || valor === undefined || valor === '') return '';
  try {
    const raw = typeof valor === 'string' ? valor : JSON.stringify(valor);
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return String(valor);
  }
};

const resumenDetalle = (log) => {
  if (!log.valor_nuevo_json) return '—';
  try {
    const raw = typeof log.valor_nuevo_json === 'string'
      ? log.valor_nuevo_json
      : JSON.stringify(log.valor_nuevo_json);
    const obj = JSON.parse(raw);
    const txt = obj.nombre || obj.mensaje || obj.cargo || obj.action;
    return txt ? String(txt).slice(0, 70) : '—';
  } catch {
    return String(log.valor_nuevo_json).slice(0, 70);
  }
};

const etiquetaAccion = (accion) =>
  accion ? accion.replace(/_/g, ' ') : '';

/* ────────────────────────────────────────────────────────────── */
/*  Componente                                                    */
/* ────────────────────────────────────────────────────────────── */
export default function Auditoria() {
  const { user } = useAuth();

  const puedeAcceder  = ['superadmin', 'coordinador_general', 'auditor'].includes(user?.rol);
  const puedeValidar  = ['superadmin', 'coordinador_general', 'coordinador_territorial', 'coordinador_sectorial'].includes(user?.rol);
  const puedePadron   = ['superadmin', 'coordinador_general'].includes(user?.rol);

  const [tab, setTab] = useState('bitacora');
  const [logs, setLogs] = useState([]);
  const [pendientes, setPendientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtroAccion, setFiltroAccion] = useState('');
  const [filtroTabla, setFiltroTabla] = useState('');
  const [mensaje, setMensaje] = useState(null);
  const [stats, setStats] = useState({});
  const [mensajeValidacion, setMensajeValidacion] = useState('');
  const [cargandoMensaje, setCargandoMensaje] = useState(true);
  const [busquedaPendientes, setBusquedaPendientes] = useState('');
  const [subTabValidacion, setSubTabValidacion] = useState('pendientes');
  const [resumenPadron, setResumenPadron] = useState([]);
  const [faltantesPadron, setFaltantesPadron] = useState([]);
  const [seccionPadronActiva, setSeccionPadronActiva] = useState(null);
  const [cargandoPadron, setCargandoPadron] = useState(false);

  // NUEVOS estados de UI
  const [detalleLog, setDetalleLog] = useState(null);
  const [enviandoSMS, setEnviandoSMS] = useState(false);
  const [paginaBitacora, setPaginaBitacora] = useState(1);
  const [copiado, setCopiado] = useState(false);

  /* ─── Carga inicial ─── */
  useEffect(() => {
    if (puedeAcceder || puedeValidar) fetchData();
    else setLoading(false);

    if (puedePadron) {
      api.get('/auditoria/padron/resumen')
        .then((r) => setResumenPadron(r.data.resumen || []))
        .catch(() => {});
    }
  }, []);

  useEffect(() => {
    const traerMensaje = async () => {
      try {
        const r = await api.get('/mensajes-validacion/obtener');
        if (r.data.success && r.data.data) {
          setMensajeValidacion(r.data.data.mensaje_whatsapp);
        }
      } catch (error) {
        console.error('Error traer mensaje:', error);
      } finally {
        setCargandoMensaje(false);
      }
    };
    traerMensaje();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [logRes, pendRes] = await Promise.all([
        puedeAcceder ? api.get('/auditoria/logs') : Promise.resolve({ data: { logs: [], stats: {} } }),
        puedeValidar ? api.get('/auditoria/pendientes') : Promise.resolve({ data: { pendientes: [] } }),
      ]);
      setLogs(logRes.data.logs || []);
      setStats(logRes.data.stats || {});
      setPendientes(pendRes.data.pendientes || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  /* ─── Exportar faltantes a CSV (Excel) ─── */
  const exportarFaltantesCSV = () => {
    if (!faltantesPadron.length) {
      setMensaje({ tipo: 'error', texto: 'No hay faltantes para exportar.' });
      setTimeout(() => setMensaje(null), 3000);
      return;
    }

    const escapar = (v) => {
      const s = String(v ?? '').replace(/"/g, '""');
      return `"${s}"`;
    };

    const headers = [
      'Sección',
      'Clave elector',
      'CURP',
      'Apellido paterno',
      'Apellido materno',
      'Nombre',
      'Sexo',
      'Fecha de nacimiento',
      'Calle',
      'Número exterior',
      'Número interior',
      'Colonia',
      'Código postal',
      'Localidad',
      'Manzana',
      'Folio',
    ];

    const filas = faltantesPadron.map((f) => [
      f.seccion || seccionPadronActiva || '',
      f.clave_elector || '',
      f.curp || '',
      f.paterno || '',
      f.materno || '',
      f.nombre || '',
      f.sexo || '',
      f.fecha_nac || '',
      f.calle || '',
      f.num_ext || '',
      f.num_int || '',
      f.colonia || '',
      f.cp || '',
      f.localidad || '',
      f.manzana || '',
      f.folio || '',
    ].map(escapar).join(','));

    const csv = '\uFEFF' + headers.map(escapar).join(',') + '\n' + filas.join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const hoy = new Date().toISOString().split('T')[0];
    a.href = url;
    a.download = `faltantes_seccion_${seccionPadronActiva}_${hoy}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const verFaltantesSeccion = async (seccion) => {
    setSeccionPadronActiva(seccion);
    setCargandoPadron(true);
    try {
      const r = await api.get(`/auditoria/padron/faltantes?seccion=${seccion}`);
      setFaltantesPadron(r.data.faltantes || []);
    } catch {
      setFaltantesPadron([]);
    } finally {
      setCargandoPadron(false);
    }
  };

  const handleEnviarSMSMasivo = async () => {
    // Filtramos a los que están en la pestaña actual (sin contactar o contactados)
    const filtrados = pendientes.filter(p => 
      `${p.nombre} ${p.apellido_paterno} ${p.telefono_principal}`.toLowerCase().includes(busquedaPendientes.toLowerCase())
    );
    const sinContactar = filtrados.filter(p => !p.whatsapp_enviado && !p.sms_enviado);
    const yaContactados = filtrados.filter(p => p.whatsapp_enviado || p.sms_enviado);
    const listaActual = subTabValidacion === 'pendientes' ? sinContactar : yaContactados;
    
    const destinatarios = listaActual.filter(p => p.telefono_principal);
    
    if (destinatarios.length === 0) return setMensaje({ tipo: 'error', texto: 'No hay personas con teléfono en esta lista.' });
    
    const minutos = Math.ceil((destinatarios.length * 15) / 60);
    if (!window.confirm(`¿Enviar SMS a las ${destinatarios.length} personas de esta lista?\n\nTomará aprox ${minutos} min.`)) return;

    try {
      setEnviandoSMS(true);
      const datosEnvio = destinatarios.map(p => ({ 
        telefono: p.telefono_principal, 
        nombre: p.nombre, 
        apellido_paterno: p.apellido_paterno, 
        persona_id: p.captura_id, // Usamos captura_id para el enlace ?start=
        id_captura: p.captura_id  // Para actualizar la BD
      }));
      
      const textoBase = mensajeValidacion || "Hola {NOMBRE}, {APELLIDO_PATERNO} apoya nuestro proyecto vinculando tu Telegram aquí:";
      const urlBot = "https://t.me/DominiumZacatlan_bot"; 

      await api.post('/sms/enviar-masivo', { destinatarios: datosEnvio, textoBase, urlBot });
      setMensaje({ tipo: 'exito', texto: `✅ Envío iniciado para ${destinatarios.length} personas.` });
      
      // Refrescamos para que se muevan a contactados
      setTimeout(() => { fetchData(); }, 3000);
    } catch (e) {
      setMensaje({ tipo: 'error', texto: e.response?.data?.error || 'Error al iniciar envío.' });
    } finally { setEnviandoSMS(false); }
  };

  const handleValidar = async (capturaId, accion) => {
    const texto = accion === 'validar' ? '✅ validar esta captura?' : '❌ rechazar esta captura?';
    if (!window.confirm(`¿Deseas ${texto}`)) return;
    try {
      await api.put(`/auditoria/validar/${capturaId}`, { accion });
      setMensaje({ tipo: 'exito', texto: accion === 'validar' ? '✅ Captura validada.' : '❌ Captura rechazada.' });
      fetchData();
      setTimeout(() => setMensaje(null), 3000);
    } catch (error) {
      setMensaje({ tipo: 'error', texto: error.response?.data?.error || 'Error.' });
    }
  };

  const abrirSMSNativo = async (c) => {
    const telOriginal = c.telefono_principal;
    if (!telOriginal) return alert('Este registro no tiene teléfono.');
    let tel = String(telOriginal).replace(/\D/g, '');
    if (tel.length === 10) tel = '52' + tel;
    let texto = mensajeValidacion || '';
    texto = texto.replace(/{NOMBRE}/gi, c.nombre || '')
                 .replace(/{APELLIDO_PATERNO}/gi, c.apellido_paterno || '')
                 .replace(/{APELLIDO_MATERNO}/gi, c.apellido_materno || '');
    const esIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const sep = esIOS ? '&' : '?';
    window.location.href = `sms:+${tel}${sep}body=${encodeURIComponent(texto)}`;
    try {
      await api.put(`/auditoria/marcar-envio/${c.captura_id}`, { canal: 'sms' });
      setPendientes(prev => prev.map(p => p.captura_id === c.captura_id ? { ...p, sms_enviado: true } : p));
    } catch {}
  };

  const abrirWhatsAppNativo = async (c) => {
    const telOriginal = c.telefono_principal;
    if (!telOriginal) return alert('Este registro no tiene teléfono.');
    let tel = String(telOriginal).replace(/\D/g, '');
    if (tel.length === 10) tel = '52' + tel;
    let texto = mensajeValidacion || '';
    texto = texto.replace(/{NOMBRE}/gi, c.nombre || '')
                 .replace(/{APELLIDO_PATERNO}/gi, c.apellido_paterno || '')
                 .replace(/{APELLIDO_MATERNO}/gi, c.apellido_materno || '');
    window.open(`https://wa.me/${tel}?text=${encodeURIComponent(texto)}`, '_blank');
    try {
      await api.put(`/auditoria/marcar-envio/${c.captura_id}`, { canal: 'whatsapp' });
      setPendientes(prev => prev.map(p => p.captura_id === c.captura_id ? { ...p, whatsapp_enviado: true } : p));
    } catch {}
  };

  const guardarMensaje = async () => {
    try {
      await api.put('/mensajes-validacion/actualizar', { mensaje_whatsapp: mensajeValidacion });
      setMensaje({ tipo: 'exito', texto: '✅ Mensaje guardado correctamente' });
      setTimeout(() => setMensaje(null), 3000);
    } catch (error) {
      setMensaje({ tipo: 'error', texto: '❌ Error al guardar: ' + error.message });
    }
  };

  const copiarJSON = (texto) => {
    navigator.clipboard.writeText(texto).then(() => {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    });
  };

  /* ─── Filtros y paginación de bitácora ─── */
  const logsFiltrados = useMemo(() => logs.filter(l => {
    const matchBusqueda = !busqueda ||
      `${l.usuario_nombre || ''} ${l.usuario_apellido || ''} ${l.tabla_afectada || ''} ${l.accion || ''}`
        .toLowerCase().includes(busqueda.toLowerCase());
    const matchAccion = !filtroAccion || l.accion === filtroAccion;
    const matchTabla = !filtroTabla || l.tabla_afectada === filtroTabla;
    return matchBusqueda && matchAccion && matchTabla;
  }), [logs, busqueda, filtroAccion, filtroTabla]);

  const totalPaginas = Math.max(1, Math.ceil(logsFiltrados.length / ITEMS_POR_PAGINA));
  const logsPagina = useMemo(() => {
    const start = (paginaBitacora - 1) * ITEMS_POR_PAGINA;
    return logsFiltrados.slice(start, start + ITEMS_POR_PAGINA);
  }, [logsFiltrados, paginaBitacora]);

  useEffect(() => { setPaginaBitacora(1); }, [busqueda, filtroAccion, filtroTabla]);

  const accionesUnicas = [...new Set(logs.map(l => l.accion))];
  const tablasUnicas  = [...new Set(logs.map(l => l.tabla_afectada))];

  /* ─── Acceso denegado ─── */
  if (!puedeAcceder && !puedeValidar) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[70vh]">
          <Lock className="text-ink-200 mb-4" size={56} />
          <p className="text-xl font-semibold text-ink-500">Acceso restringido</p>
        </div>
      </Layout>
    );
  }

  /* ═══════════════════════════════════════════════════════════ */
  /*  RENDER                                                      */
  /* ═══════════════════════════════════════════════════════════ */
  return (
    <Layout>
      <div className="p-4 md:p-6 max-w-7xl mx-auto w-full">

        {/* ── Header ── */}
        <div className="mb-5 flex items-center justify-between flex-wrap gap-3 animate-fade-in-up">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-ink-900 flex items-center gap-2">
              <Shield className="text-guinda-700" size={28} />
              Auditoría y Bitácora
            </h2>
            <p className="text-ink-500 text-sm mt-1">Control y validación del sistema</p>
          </div>
          <button onClick={fetchData}
            className="bg-white hover:bg-ink-50 p-2.5 rounded-xl border border-ink-200 transition shadow-soft">
            <RefreshCw size={16} className={`text-ink-600 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* ── Mensaje global ── */}
        {mensaje && (
          <div className={`mb-4 p-4 rounded-xl text-sm font-medium border ${
            mensaje.tipo === 'exito'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}>{mensaje.texto}</div>
        )}

        {/* ── Stats ── */}
        {puedeAcceder && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {[
              { label: 'Total acciones',  valor: stats.total   || 0, color: 'border-ink-400',    icono: '📋' },
              { label: 'Inserciones',     valor: stats.inserts || 0, color: 'border-verde-600',  icono: '➕' },
              { label: 'Actualizaciones', valor: stats.updates || 0, color: 'border-blue-500',   icono: '✏️' },
              { label: 'Eliminaciones',   valor: stats.deletes || 0, color: 'border-red-500',    icono: '🗑️' },
            ].map((s, i) => (
              <div key={i} className={`bg-white rounded-xl md:rounded-2xl shadow-soft border border-ink-100 p-3 text-center border-l-4 ${s.color}`}>
                <p className="text-xl mb-1">{s.icono}</p>
                <p className="text-2xl font-bold text-ink-900">{s.valor}</p>
                <p className="text-xs text-ink-500 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* ── Tabs ── */}
        <div className="flex gap-2 mb-6 border-b border-ink-100 overflow-x-auto">
          {[
            puedeAcceder && { key: 'bitacora',   label: '📋 Bitácora de Acciones' },
            puedeValidar && { key: 'validacion', label: `✅ Validar Capturas ${pendientes.length > 0 ? `(${pendientes.length})` : ''}` },
            puedePadron  && { key: 'padron',     label: '🗳 Padrón Electoral' },
          ].filter(Boolean).map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-5 py-2.5 font-medium border-b-2 transition text-sm whitespace-nowrap ${
                tab === t.key
                  ? 'border-guinda-700 text-guinda-700'
                  : 'border-transparent text-ink-500 hover:text-ink-700'
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ══════════════════════════════════════════════════════ */}
        {/*  BITÁCORA                                              */}
        {/* ══════════════════════════════════════════════════════ */}
        {tab === 'bitacora' && puedeAcceder && (
          <div>
            {/* Filtros */}
            <div className="flex flex-wrap gap-3 mb-4">
              <div className="relative flex-1 min-w-48">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
                  placeholder="Buscar por usuario, acción o tabla..."
                  className="w-full pl-9 pr-3 py-2.5 border border-ink-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-guinda-700/20 focus:border-guinda-700 transition" />
              </div>
              <select value={filtroAccion} onChange={(e) => setFiltroAccion(e.target.value)}
                className="px-3 py-2.5 border border-ink-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-guinda-700/20 focus:border-guinda-700">
                <option value="">Todas las acciones</option>
                {accionesUnicas.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
              <select value={filtroTabla} onChange={(e) => setFiltroTabla(e.target.value)}
                className="px-3 py-2.5 border border-ink-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-guinda-700/20 focus:border-guinda-700">
                <option value="">Todas las tablas</option>
                {tablasUnicas.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            {/* Vista desktop */}
            <div className="hidden md:block bg-white rounded-2xl shadow-soft border border-ink-100 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-ink-50 border-b border-ink-100">
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold text-ink-600 text-xs uppercase tracking-wide">Fecha</th>
                    <th className="text-left py-3 px-4 font-semibold text-ink-600 text-xs uppercase tracking-wide">Usuario</th>
                    <th className="text-left py-3 px-4 font-semibold text-ink-600 text-xs uppercase tracking-wide">Acción</th>
                    <th className="text-left py-3 px-4 font-semibold text-ink-600 text-xs uppercase tracking-wide">Tabla</th>
                    <th className="text-left py-3 px-4 font-semibold text-ink-600 text-xs uppercase tracking-wide">Detalle</th>
                    <th className="text-left py-3 px-4 font-semibold text-ink-600 text-xs uppercase tracking-wide">IP</th>
                    <th className="py-3 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="7" className="py-10 text-center text-ink-400 text-sm">Cargando registros...</td></tr>
                  ) : logsPagina.length === 0 ? (
                    <tr><td colSpan="7" className="py-10 text-center text-ink-400 text-sm">No hay registros que coincidan</td></tr>
                  ) : logsPagina.map((l, i) => (
                    <tr key={i} className="border-b border-ink-100 hover:bg-ink-50 transition-colors">
                      <td className="py-3 px-4 text-xs text-ink-500 whitespace-nowrap">{formatearFecha(l.fecha)}</td>
                      <td className="py-3 px-4">
                        <p className="font-medium text-xs text-ink-900">{l.usuario_nombre} {l.usuario_apellido || ''}</p>
                        <p className="text-xs text-ink-400 capitalize">{l.usuario_rol?.replace(/_/g, ' ')}</p>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-bold border uppercase tracking-wide ${
                          ACCIONES_COLOR[l.accion] || 'bg-gray-100 text-gray-600 border-gray-200'
                        }`}>
                          {etiquetaAccion(l.accion)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-xs text-ink-600 font-mono">{l.tabla_afectada}</td>
                      <td className="py-3 px-4 text-xs text-ink-500 max-w-xs truncate">{resumenDetalle(l)}</td>
                      <td className="py-3 px-4 text-xs text-ink-400 font-mono">
                        {esIPLocal(l.ip) ? '—' : l.ip}
                      </td>
                      <td className="py-3 px-4">
                        <button onClick={() => setDetalleLog(l)}
                          className="p-1.5 rounded-lg hover:bg-guinda-700/10 text-ink-500 hover:text-guinda-700 transition"
                          title="Ver detalle">
                          <Eye size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Vista móvil */}
            <div className="md:hidden space-y-3">
              {loading ? (
                <div className="bg-white rounded-2xl p-8 text-center text-ink-400 text-sm">Cargando...</div>
              ) : logsPagina.length === 0 ? (
                <div className="bg-white rounded-2xl p-8 text-center text-ink-400 text-sm">No hay registros</div>
              ) : logsPagina.map((l, i) => (
                <div key={i} className="bg-white rounded-2xl shadow-soft border border-ink-100 p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-bold border uppercase tracking-wide ${
                      ACCIONES_COLOR[l.accion] || 'bg-gray-100 text-gray-600 border-gray-200'
                    }`}>
                      {etiquetaAccion(l.accion)}
                    </span>
                    <button onClick={() => setDetalleLog(l)}
                      className="p-1.5 rounded-lg bg-ink-50 text-ink-500 hover:text-guinda-700">
                      <Eye size={14} />
                    </button>
                  </div>
                  <p className="font-semibold text-ink-900 text-sm">{l.usuario_nombre} {l.usuario_apellido || ''}</p>
                  <p className="text-xs text-ink-400 capitalize mb-2">{l.usuario_rol?.replace(/_/g, ' ')}</p>
                  <div className="text-xs text-ink-500 space-y-1">
                    <p><span className="text-ink-400">Tabla:</span> <span className="font-mono">{l.tabla_afectada}</span></p>
                    <p><span className="text-ink-400">Detalle:</span> {resumenDetalle(l)}</p>
                    <p><span className="text-ink-400">Fecha:</span> {formatearFecha(l.fecha)}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Paginación */}
            {!loading && logsFiltrados.length > 0 && (
              <div className="flex items-center justify-between mt-4 flex-wrap gap-3">
                <p className="text-xs text-ink-500">
                  Mostrando <span className="font-semibold text-ink-700">{logsPagina.length}</span> de{' '}
                  <span className="font-semibold text-ink-700">{logsFiltrados.length}</span> registros
                  {logsFiltrados.length !== logs.length && (
                    <span className="text-ink-400"> (filtrado de {logs.length})</span>
                  )}
                </p>
                {totalPaginas > 1 && (
                  <div className="flex items-center gap-1">
                    <button onClick={() => setPaginaBitacora(p => Math.max(1, p - 1))}
                      disabled={paginaBitacora === 1}
                      className="p-2 rounded-lg border border-ink-200 bg-white hover:bg-ink-50 disabled:opacity-40 disabled:cursor-not-allowed transition">
                      <ChevronLeft size={16} className="text-ink-600" />
                    </button>
                    <span className="text-xs text-ink-600 px-3 py-2 font-medium">
                      {paginaBitacora} / {totalPaginas}
                    </span>
                    <button onClick={() => setPaginaBitacora(p => Math.min(totalPaginas, p + 1))}
                      disabled={paginaBitacora === totalPaginas}
                      className="p-2 rounded-lg border border-ink-200 bg-white hover:bg-ink-50 disabled:opacity-40 disabled:cursor-not-allowed transition">
                      <ChevronRight size={16} className="text-ink-600" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════ */}
        {/*  VALIDACIÓN DE CAPTURAS                                */}
        {/* ══════════════════════════════════════════════════════ */}
        {tab === 'validacion' && puedeValidar && (
          <div className="space-y-4">

            {/* Panel mensaje WhatsApp */}
            <div className="bg-guinda-700/5 border border-guinda-700/20 rounded-2xl p-4 md:p-5">
              <label className="block text-sm font-semibold text-ink-900 mb-2 flex items-center gap-2">
                📝 Mensaje de Validación por WhatsApp
              </label>
              <textarea
                value={mensajeValidacion}
                onChange={(e) => setMensajeValidacion(e.target.value)}
                disabled={cargandoMensaje}
                className="w-full h-32 p-3 border border-ink-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-guinda-700/20 focus:border-guinda-700 resize-none text-sm bg-white"
                placeholder="Escribe el mensaje que se enviará..."
              />
              <div className="flex items-center justify-between flex-wrap gap-3 mt-3">
                <p className="text-xs text-ink-500">
                  💡 Usa <code className="px-1 py-0.5 bg-white rounded text-guinda-700 font-mono">{'{NOMBRE}'}</code>,{' '}
                  <code className="px-1 py-0.5 bg-white rounded text-guinda-700 font-mono">{'{APELLIDO_PATERNO}'}</code> o{' '}
                  <code className="px-1 py-0.5 bg-white rounded text-guinda-700 font-mono">{'{APELLIDO_MATERNO}'}</code>
                </p>
                <div className="flex gap-2">
                  <button onClick={guardarMensaje} disabled={cargandoMensaje}
                    className="px-4 py-2 bg-guinda-700 hover:bg-guinda-800 text-white rounded-xl text-sm font-medium transition shadow-soft disabled:opacity-50">
                    💾 Guardar Mensaje
                  </button>
                  <button onClick={handleEnviarSMSMasivo} disabled={enviandoSMS}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold transition shadow-soft disabled:opacity-50">
                    <Send size={14} /> {enviandoSMS ? 'Iniciando...' : 'Enviar SMS Masivo'}
                  </button>
                </div>
              </div>
            </div>

            {pendientes.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-soft border border-ink-100 p-12 text-center">
                <CheckCircle className="text-verde-600 mx-auto mb-3" size={48} />
                <p className="text-ink-700 font-medium">¡Todo al día!</p>
                <p className="text-ink-400 text-sm mt-1">No hay capturas pendientes de validación</p>
              </div>
            ) : (
              <div>
                <p className="text-sm text-ink-500 mb-4">
                  <span className="font-semibold text-ink-900">{pendientes.length}</span> captura(s) pendiente(s) de revisión
                </p>

                <div className="mb-4">
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                    <input value={busquedaPendientes} onChange={(e) => setBusquedaPendientes(e.target.value)}
                      placeholder="Buscar por nombre, teléfono o sección..."
                      className="w-full pl-9 pr-3 py-2.5 border border-ink-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-guinda-700/20 focus:border-guinda-700" />
                  </div>
                </div>

                {(() => {
                  const filtradas = pendientes.filter(p =>
                    `${p.nombre} ${p.apellido_paterno} ${p.telefono_principal}`
                      .toLowerCase().includes(busquedaPendientes.toLowerCase()));
                  const sinContactar = filtradas.filter(p => !p.whatsapp_enviado && !p.sms_enviado);
                  const yaContactados = filtradas.filter(p => p.whatsapp_enviado || p.sms_enviado);
                  const listaActual = subTabValidacion === 'pendientes' ? sinContactar : yaContactados;

                  return (
                    <>
                      <div className="flex gap-2 mb-4 border-b border-ink-100 overflow-x-auto">
                        <button onClick={() => setSubTabValidacion('pendientes')}
                          className={`px-4 py-2 text-sm font-semibold border-b-2 transition whitespace-nowrap ${
                            subTabValidacion === 'pendientes'
                              ? 'border-amber-500 text-amber-700'
                              : 'border-transparent text-ink-500 hover:text-ink-700'
                          }`}>
                          ⏳ Sin contactar ({sinContactar.length})
                        </button>
                        <button onClick={() => setSubTabValidacion('contactados')}
                          className={`px-4 py-2 text-sm font-semibold border-b-2 transition whitespace-nowrap ${
                            subTabValidacion === 'contactados'
                              ? 'border-guinda-700 text-guinda-700'
                              : 'border-transparent text-ink-500 hover:text-ink-700'
                          }`}>
                          📨 Ya contactados ({yaContactados.length})
                        </button>
                      </div>

                      {listaActual.length === 0 ? (
                        <div className="bg-white rounded-2xl shadow-soft border border-ink-100 p-8 text-center text-ink-400 text-sm">
                          {subTabValidacion === 'pendientes'
                            ? 'No hay nadie sin contactar 🎉'
                            : 'Todavía no le has mandado mensaje a nadie'}
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {listaActual.map((c, i) => (
                            <div key={i} className={`rounded-2xl shadow-soft border p-4 transition ${
                              subTabValidacion === 'contactados'
                                ? 'bg-guinda-700/5 border-guinda-700/20'
                                : 'bg-white border-ink-100'
                            }`}>
                              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <p className="font-semibold text-ink-900">
                                      {c.nombre} {c.apellido_paterno} {c.apellido_materno || ''}
                                    </p>
                                    {subTabValidacion === 'contactados' ? (
                                      <span className="px-2 py-0.5 bg-rose-100 text-rose-700 border border-rose-200 rounded-full text-[10px] font-bold uppercase tracking-wide">
                                        📨 Esperando respuesta
                                      </span>
                                    ) : (
                                      <span className="px-2 py-0.5 bg-amber-100 text-amber-700 border border-amber-200 rounded-full text-[10px] font-bold uppercase tracking-wide">
                                        ⏳ {c.estatus_captura}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-ink-500">
                                    <span>📞 {c.telefono_principal}</span>
                                    {c.numero_seccion && <span>🗺 Sección {c.numero_seccion}</span>}
                                    {c.nombre_estructura && <span>🏛 {c.nombre_estructura}</span>}
                                    <span>👤 {c.capturado_por}</span>
                                    <span>📅 {new Date(c.fecha_captura).toLocaleDateString('es-MX')}</span>
                                  </div>
                                  {c.motivo_estatus && (
                                    <p className="text-xs text-amber-700 mt-2 font-medium">⚠️ {c.motivo_estatus}</p>
                                  )}
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                  {c.telefono_principal && (
                                    <>
                                      <button onClick={() => abrirWhatsAppNativo(c)}
                                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-white transition shadow-soft"
                                        style={{ background: c.whatsapp_enviado ? '#128C7E' : '#25D366' }}>
                                        {c.whatsapp_enviado ? '✅' : '📱'} WhatsApp
                                      </button>
                                      <button onClick={() => abrirSMSNativo(c)}
                                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-white transition shadow-soft"
                                        style={{ background: c.sms_enviado ? '#1a4a8a' : '#2563a8' }}>
                                        {c.sms_enviado ? '✅' : '💬'} SMS
                                      </button>
                                    </>
                                  )}
                                  <button onClick={() => handleValidar(c.captura_id, 'validar')}
                                    className="flex items-center gap-1.5 px-3 py-2 bg-verde-600 hover:bg-verde-700 text-white rounded-xl text-xs font-semibold transition shadow-soft">
                                    <CheckCircle size={14} /> Validar
                                  </button>
                                  <button onClick={() => handleValidar(c.captura_id, 'rechazar')}
                                    className="flex items-center gap-1.5 px-3 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-xl text-xs font-semibold transition">
                                    <XCircle size={14} /> Rechazar
                                  </button>
                                </div>
                              </div>
                              {c.respuesta_recibida && (
                                <div className="mt-3 px-3 py-2 bg-white border border-guinda-700/20 rounded-xl text-xs">
                                  <span className="font-semibold text-guinda-700">Respondió:</span>{' '}
                                  <span className="text-ink-700">{c.respuesta_recibida}</span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════ */}
        {/*  PADRÓN ELECTORAL                                      */}
        {/* ══════════════════════════════════════════════════════ */}
        {tab === 'padron' && puedePadron && (
          <div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-xs text-amber-800">
              🔒 Información confidencial — contiene domicilios completos del padrón. Visible solo para superadmin y coordinador general.
            </div>

            {resumenPadron.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-soft border border-ink-100 p-10 text-center text-ink-400 text-sm">
                No hay datos del padrón disponibles
              </div>
            ) : (
              <>
                {/* Desktop */}
                <div className="hidden md:block bg-white rounded-2xl shadow-soft border border-ink-100 overflow-hidden mb-4">
                  <table className="w-full text-sm">
                    <thead className="bg-ink-50 border-b border-ink-100">
                      <tr>
                        <th className="text-left py-3 px-4 font-semibold text-ink-600 text-xs uppercase tracking-wide">Sección</th>
                        <th className="text-left py-3 px-4 font-semibold text-ink-600 text-xs uppercase tracking-wide">Total padrón</th>
                        <th className="text-left py-3 px-4 font-semibold text-verde-700 text-xs uppercase tracking-wide">Ya en sistema</th>
                        <th className="text-left py-3 px-4 font-semibold text-red-600 text-xs uppercase tracking-wide">Faltan por tocar</th>
                        <th className="text-left py-3 px-4 font-semibold text-ink-600 text-xs uppercase tracking-wide">% Cobertura</th>
                        <th className="text-left py-3 px-4"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {resumenPadron.map((r, i) => {
                        const pct = r.total_padron > 0 ? Math.round((r.total_contactados / r.total_padron) * 100) : 0;
                        return (
                          <tr key={i} className="border-b border-ink-100 hover:bg-ink-50 transition-colors">
                            <td className="py-3 px-4 font-bold text-ink-900">{r.seccion}</td>
                            <td className="py-3 px-4 text-ink-700">{r.total_padron}</td>
                            <td className="py-3 px-4 text-verde-700 font-bold">{r.total_contactados}</td>
                            <td className="py-3 px-4 text-red-600 font-bold">{r.faltantes}</td>
                            <td className="py-3 px-4 text-ink-700 font-medium">{pct}%</td>
                            <td className="py-3 px-4">
                              <button onClick={() => verFaltantesSeccion(r.seccion)}
                                className="px-3 py-1.5 bg-guinda-700 hover:bg-guinda-800 text-white rounded-lg text-xs font-semibold transition shadow-soft">
                                Ver faltantes
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Móvil */}
                <div className="md:hidden space-y-3 mb-4">
                  {resumenPadron.map((r, i) => {
                    const pct = r.total_padron > 0 ? Math.round((r.total_contactados / r.total_padron) * 100) : 0;
                    return (
                      <div key={i} className="bg-white rounded-2xl shadow-soft border border-ink-100 p-4">
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-bold text-ink-900 text-lg">Sección {r.seccion}</p>
                          <span className="text-xs font-bold text-ink-600 bg-ink-50 px-2 py-1 rounded-full">{pct}%</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs mb-3">
                          <div><p className="text-ink-400">Padrón</p><p className="font-semibold text-ink-800">{r.total_padron}</p></div>
                          <div><p className="text-ink-400">En sistema</p><p className="font-semibold text-verde-700">{r.total_contactados}</p></div>
                          <div><p className="text-ink-400">Faltan</p><p className="font-semibold text-red-600">{r.faltantes}</p></div>
                        </div>
                        <button onClick={() => verFaltantesSeccion(r.seccion)}
                          className="w-full py-2 bg-guinda-700 hover:bg-guinda-800 text-white rounded-xl text-xs font-semibold transition">
                          Ver faltantes
                        </button>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {seccionPadronActiva && (
              <div className="bg-white rounded-2xl shadow-soft border border-ink-100 p-4">
                <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                  <h3 className="font-semibold text-ink-900">
                    Faltantes en Sección {seccionPadronActiva}{' '}
                    {cargandoPadron ? '(cargando...)' : `(${faltantesPadron.length})`}
                  </h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={exportarFaltantesCSV}
                      disabled={cargandoPadron || faltantesPadron.length === 0}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-verde-600 hover:bg-verde-700 text-white rounded-lg text-xs font-semibold transition shadow-soft disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Descargar lista en Excel (CSV)">
                      <Download size={14} />
                      Exportar a Excel
                    </button>
                    <button onClick={() => { setSeccionPadronActiva(null); setFaltantesPadron([]); }}
                      className="p-1.5 rounded-lg hover:bg-ink-100 transition">
                      <X size={16} className="text-ink-500" />
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                  <table className="w-full text-xs min-w-[600px]">
                    <thead className="bg-ink-50 border-b border-ink-100 sticky top-0">
                      <tr>
                        <th className="text-left py-2 px-3 font-semibold text-ink-600">Nombre</th>
                        <th className="text-left py-2 px-3 font-semibold text-ink-600">Domicilio</th>
                        <th className="text-left py-2 px-3 font-semibold text-ink-600">Colonia / Localidad</th>
                        <th className="text-left py-2 px-3 font-semibold text-ink-600">Clave Elector</th>
                      </tr>
                    </thead>
                    <tbody>
                      {faltantesPadron.map((f, i) => (
                        <tr key={i} className="border-b border-ink-100 hover:bg-ink-50 transition-colors">
                          <td className="py-2 px-3 text-ink-800">{f.nombre} {f.paterno} {f.materno || ''}</td>
                          <td className="py-2 px-3 text-ink-600">{f.calle} {f.num_ext || ''}</td>
                          <td className="py-2 px-3 text-ink-600">{f.colonia || f.localidad}</td>
                          <td className="py-2 px-3 font-mono text-ink-500">{f.clave_elector}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        <p className="text-center text-ink-400 text-xs mt-8">
          DOMINIUM © 2026 — Zacatlán, Puebla | GARCOR Design
        </p>
      </div>

      {/* ══════════════════════════════════════════════════════ */}
      {/*  MODAL DETALLE DE BITÁCORA                             */}
      {/* ══════════════════════════════════════════════════════ */}
      {detalleLog && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setDetalleLog(null)}>
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}>

            {/* Header del modal */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-ink-100 bg-ink-50">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-guinda-700/10 flex items-center justify-center">
                  <Shield size={16} className="text-guinda-700" />
                </div>
                <div>
                  <h3 className="font-bold text-ink-900 text-sm">Detalle de acción</h3>
                  <p className="text-xs text-ink-500">{formatearFecha(detalleLog.fecha)}</p>
                </div>
              </div>
              <button onClick={() => setDetalleLog(null)}
                className="p-2 rounded-lg hover:bg-ink-100 transition">
                <X size={18} className="text-ink-600" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 overflow-y-auto space-y-4">
              {/* Ficha */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-ink-50 rounded-xl p-3">
                  <p className="text-[10px] uppercase tracking-wide text-ink-400 font-semibold mb-1">Usuario</p>
                  <p className="text-sm font-medium text-ink-900">
                    {detalleLog.usuario_nombre} {detalleLog.usuario_apellido || ''}
                  </p>
                  <p className="text-xs text-ink-500 capitalize">{detalleLog.usuario_rol?.replace(/_/g, ' ')}</p>
                </div>
                <div className="bg-ink-50 rounded-xl p-3">
                  <p className="text-[10px] uppercase tracking-wide text-ink-400 font-semibold mb-1">Acción</p>
                  <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-bold border uppercase tracking-wide ${
                    ACCIONES_COLOR[detalleLog.accion] || 'bg-gray-100 text-gray-600 border-gray-200'
                  }`}>
                    {etiquetaAccion(detalleLog.accion)}
                  </span>
                </div>
                <div className="bg-ink-50 rounded-xl p-3">
                  <p className="text-[10px] uppercase tracking-wide text-ink-400 font-semibold mb-1">Tabla afectada</p>
                  <p className="text-sm font-mono text-ink-800">{detalleLog.tabla_afectada || '—'}</p>
                </div>
                <div className="bg-ink-50 rounded-xl p-3">
                  <p className="text-[10px] uppercase tracking-wide text-ink-400 font-semibold mb-1">IP</p>
                  <p className="text-sm font-mono text-ink-800">
                    {esIPLocal(detalleLog.ip) ? 'localhost' : detalleLog.ip}
                  </p>
                </div>
              </div>

              {/* Valor anterior */}
              {detalleLog.valor_anterior_json && (
                <div>
                  <p className="text-xs font-semibold text-ink-600 mb-1.5">Valor anterior</p>
                  <pre className="bg-red-50 border border-red-100 rounded-xl p-3 text-xs text-red-900 overflow-x-auto max-h-40 font-mono">
                    {formatearJSON(detalleLog.valor_anterior_json)}
                  </pre>
                </div>
              )}

              {/* Valor nuevo */}
              {detalleLog.valor_nuevo_json && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-xs font-semibold text-ink-600">Valor nuevo</p>
                    <button onClick={() => copiarJSON(formatearJSON(detalleLog.valor_nuevo_json))}
                      className="flex items-center gap-1 text-xs text-guinda-700 hover:text-guinda-800 font-medium transition">
                      {copiado ? <><Check size={12} /> Copiado</> : <><Copy size={12} /> Copiar</>}
                    </button>
                  </div>
                  <pre className="bg-verde-600/5 border border-verde-600/20 rounded-xl p-3 text-xs text-ink-800 overflow-x-auto max-h-60 font-mono">
                    {formatearJSON(detalleLog.valor_nuevo_json)}
                  </pre>
                </div>
              )}

              {!detalleLog.valor_anterior_json && !detalleLog.valor_nuevo_json && (
                <div className="text-center py-6 text-ink-400 text-xs">
                  Sin payload adicional para esta acción
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
